/** Geo helpers shared by the live-radar UI and the geolocation store. */

export interface LatLon {
  lat: number;
  lon: number;
}

const EARTH_RADIUS_M = 6371e3;

/** Great-circle distance between two points in metres (haversine). */
export function haversineMeters(a: LatLon, b: LatLon): number {
  const φ1 = (a.lat * Math.PI) / 180;
  const φ2 = (b.lat * Math.PI) / 180;
  const Δφ = ((b.lat - a.lat) * Math.PI) / 180;
  const Δλ = ((b.lon - a.lon) * Math.PI) / 180;

  const h =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Square bounding box of half-side `radiusM` around `center`, returned as the backend's
 * [minLat, minLon, maxLat, maxLon]. The circle of radius `radiusM` is inscribed in this box;
 * corner events that fall outside the circle are dropped client-side by `haversineMeters`.
 */
export function bboxAround(
  center: LatLon,
  radiusM: number,
): [number, number, number, number] {
  const dLat = (radiusM / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLon =
    (radiusM / (EARTH_RADIUS_M * Math.cos((center.lat * Math.PI) / 180))) *
    (180 / Math.PI);
  return [center.lat - dLat, center.lon - dLon, center.lat + dLat, center.lon + dLon];
}

/** Human-friendly distance label: "740 м" under 1 km, "2.3 км" otherwise. */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters / 10) * 10} м`;
  return `${(meters / 1000).toFixed(1)} км`;
}
